/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { checkWinner, getAIMove, createEmptyBoard } from "@/utils/aiLogic";
import { checkSOSPatterns, isSOSGameOver, getAllSOSPatterns, getSOSAIMove, createEmptySOSBoard } from "@/utils/sosLogic";
import { announcePlayerTurn, announceWinner, announceDraw, playThunderSound, playZapSound } from "@/utils/soundEffects";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useBoardGame() {
  const [gameStarted, setGameStarted] = useState(false);
  const [board, setBoard] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState("X");
  const [winner, setWinner] = useState(null);
  const [winningCells, setWinningCells] = useState([]);
  const [moveHistory, setMoveHistory] = useState([]);
  const [gameStartTime, setGameStartTime] = useState(null);

  const [mode, setMode] = useState("classic");
  const [playerType, setPlayerType] = useState("ai");
  const [aiDifficulty, setAiDifficulty] = useState("medium");
  const [boardSize, setBoardSize] = useState(3);

  const [playerXName, setPlayerXName] = useState("Player X");
  const [playerOName, setPlayerOName] = useState("Player O");
  const [showPlayerNamesDialog, setShowPlayerNamesDialog] = useState(false);

  const [xWins, setXWins] = useState(() => {
    const saved = localStorage.getItem('tictactoe_xWins');
    return saved ? parseInt(saved) : 0;
  });
  const [oWins, setOWins] = useState(() => {
    const saved = localStorage.getItem('tictactoe_oWins');
    return saved ? parseInt(saved) : 0;
  });
  const [draws, setDraws] = useState(() => {
    const saved = localStorage.getItem('tictactoe_draws');
    return saved ? parseInt(saved) : 0;
  });

  const [hasSavedGame, setHasSavedGame] = useState(false);

  // SOS state
  const [sosScores, setSosScores] = useState([0, 0, 0, 0]);
  const [sosPatterns, setSosPatterns] = useState([]);
  const [sosCurrentPlayer, setSosCurrentPlayer] = useState(1);
  const [sosPlayerCount, setSosPlayerCount] = useState(2);
  const [sosPlayerNames, setSosPlayerNames] = useState(["Pemain 1", "Pemain 2", "Pemain 3", "Pemain 4"]);

  // UI state
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [sosConfetti, setSosConfetti] = useState(false);

  const winLength = mode === "classic" ? 3 : 5;

  useEffect(() => {
    const savedGame = localStorage.getItem('tictactoe_savedGame');
    if (savedGame) setHasSavedGame(true);
  }, []);

  useEffect(() => {
    localStorage.setItem('tictactoe_xWins', xWins.toString());
    localStorage.setItem('tictactoe_oWins', oWins.toString());
    localStorage.setItem('tictactoe_draws', draws.toString());
  }, [xWins, oWins, draws]);

  const initializeBoard = useCallback(() => {
    if (mode === "sos") {
      setBoard(createEmptySOSBoard(boardSize));
      setSosScores([0, 0, 0, 0]);
      setSosPatterns([]);
      setSosCurrentPlayer(1);
    } else {
      setBoard(createEmptyBoard(boardSize));
    }
    setCurrentPlayer("X");
    setWinner(null);
    setWinningCells([]);
    setMoveHistory([]);
    setShowWinnerModal(false);
    setGameStartTime(Date.now());
    localStorage.removeItem('tictactoe_savedGame');
    setHasSavedGame(false);
  }, [boardSize, mode]);

  const saveGame = useCallback(() => {
    const gameState = {
      board, currentPlayer, moveHistory, mode, playerType,
      aiDifficulty, boardSize, playerXName, playerOName, gameStartTime,
    };
    localStorage.setItem('tictactoe_savedGame', JSON.stringify(gameState));
    setHasSavedGame(true);
    toast.success("Game saved!");
  }, [board, currentPlayer, moveHistory, mode, playerType, aiDifficulty, boardSize, playerXName, playerOName, gameStartTime]);

  const loadSavedGame = useCallback(() => {
    const saved = localStorage.getItem('tictactoe_savedGame');
    if (saved) {
      const gs = JSON.parse(saved);
      setBoard(gs.board);
      setCurrentPlayer(gs.currentPlayer);
      setMoveHistory(gs.moveHistory);
      setMode(gs.mode);
      setPlayerType(gs.playerType);
      setAiDifficulty(gs.aiDifficulty);
      setBoardSize(gs.boardSize);
      setPlayerXName(gs.playerXName || "Player X");
      setPlayerOName(gs.playerOName || "Player O");
      setGameStartTime(gs.gameStartTime);
      setGameStarted(true);
      setWinner(null);
      setWinningCells([]);
      toast.success("Game loaded!");
    }
  }, []);

  const resetScores = () => {
    setXWins(0); setOWins(0); setDraws(0);
    localStorage.removeItem('tictactoe_savedGame');
    setHasSavedGame(false);
    toast.info("Scores reset!");
  };

  const handleStartGame = () => {
    if (playerType === "pvp") {
      setShowPlayerNamesDialog(true);
    } else {
      if (mode === "sos") {
        setSosPlayerNames(["Kamu", "AI", "Pemain 3", "Pemain 4"]);
        setSosPlayerCount(2);
      }
      setGameStarted(true);
      initializeBoard();
      const modeNames = { classic: "Classic", gomoku: "Gomoku", sos: "SOS" };
      toast.success(`Game started! Mode: ${modeNames[mode]}`);
    }
  };

  const handlePlayerNamesSubmit = (xName, oName, allNames) => {
    setPlayerXName(xName);
    setPlayerOName(oName);
    if (mode === "sos" && allNames && allNames.length > 0) {
      setSosPlayerNames(allNames.map((n, i) => n || `Pemain ${i + 1}`));
    } else {
      setSosPlayerNames([xName, oName, "Pemain 3", "Pemain 4"]);
    }
    setShowPlayerNamesDialog(false);
    setGameStarted(true);
    initializeBoard();
    if (mode === "sos" && sosPlayerCount > 2) {
      toast.success(`${sosPlayerCount} Pemain - Ayo main SOS!`);
    } else {
      toast.success(`${xName} vs ${oName} - Let's play!`);
    }
    if (soundEnabled) setTimeout(() => announcePlayerTurn(xName), 500);
  };

  // Finish SOS game
  const finishSOSGame = useCallback((finalScores) => {
    // Guard: prevent finishing twice (defensive against race conditions when board fills)
    setWinner(prev => {
      if (prev) return prev; // already finished, keep previous winner
      const relevantScores = finalScores.slice(0, sosPlayerCount);
      const maxScore = Math.max(...relevantScores);
      const winners = [];
      relevantScores.forEach((s, i) => { if (s === maxScore) winners.push(i); });

      // Tie when zero score OR more than one player shares the max score
      let gameWinner, winnerName;
      if (maxScore === 0 || winners.length > 1) {
        gameWinner = "draw";
      } else {
        gameWinner = `player${winners[0] + 1}`;
        winnerName = sosPlayerNames[winners[0]];
      }
      if (soundEnabled) {
        setTimeout(() => {
          if (gameWinner === "draw") announceDraw();
          else announceWinner(winnerName);
        }, 500);
      }
      return gameWinner;
    });
    localStorage.removeItem('tictactoe_savedGame');
    setHasSavedGame(false);
    setTimeout(() => setShowWinnerModal(true), 500);
  }, [sosPlayerCount, sosPlayerNames, soundEnabled]);

  // SOS cell click
  const handleSOSCellClick = useCallback((row, col, letter) => {
    if (winner || board[row][col] || isAIThinking) return;
    if (playerType === "ai" && sosCurrentPlayer === 2) return;

    if (soundEnabled) { playThunderSound(); playZapSound(); }

    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = letter;
    setBoard(newBoard);
    setMoveHistory(prev => [...prev, { row, col, letter, player: sosCurrentPlayer, board: board.map(r => [...r]) }]);

    const newPatterns = checkSOSPatterns(newBoard, boardSize, row, col);
    const pointsScored = newPatterns.length;
    const gameOver = isSOSGameOver(newBoard, boardSize);

    if (pointsScored > 0) {
      const newScores = [...sosScores];
      newScores[sosCurrentPlayer - 1] += pointsScored;
      setSosScores(newScores);
      setSosPatterns(getAllSOSPatterns(newBoard, boardSize));
      setSosConfetti(true);
      setTimeout(() => setSosConfetti(false), 2500);
      const playerName = sosPlayerNames[sosCurrentPlayer - 1];
      toast.success(`${playerName} scored ${pointsScored} SOS!`);
      if (gameOver) finishSOSGame(newScores);
      // SOS rule: scoring player keeps their turn (no advance)
    } else if (gameOver) {
      // Board is full without a scoring move — finalize immediately,
      // do NOT advance the player (prevents UI flicker / wrong "next turn")
      finishSOSGame(sosScores);
    } else {
      const nextPlayer = (sosCurrentPlayer % sosPlayerCount) + 1;
      setSosCurrentPlayer(nextPlayer);
      if (soundEnabled && playerType === "pvp") {
        const nextPlayerName = sosPlayerNames[nextPlayer - 1];
        setTimeout(() => announcePlayerTurn(nextPlayerName), 300);
      }
    }
  }, [winner, board, isAIThinking, playerType, sosCurrentPlayer, boardSize, soundEnabled, sosPlayerNames, sosScores, sosPlayerCount, finishSOSGame]);

  // SOS AI effect
  useEffect(() => {
    if (gameStarted && mode === "sos" && playerType === "ai" && sosCurrentPlayer === 2 && !winner && sosPlayerCount === 2) {
      let hasEmpty = false;
      for (let r = 0; r < boardSize && !hasEmpty; r++)
        for (let c = 0; c < boardSize && !hasEmpty; c++)
          if (!board[r]?.[c]) hasEmpty = true;
      if (!hasEmpty) return;

      setIsAIThinking(true);
      const thinkTime = aiDifficulty === "easy" ? 600 : aiDifficulty === "medium" ? 900 : 1200;

      const tid = setTimeout(() => {
        const aiMove = getSOSAIMove(board, boardSize, aiDifficulty);
        if (aiMove && !board[aiMove.row][aiMove.col]) {
          if (soundEnabled) { playThunderSound(); playZapSound(); }
          const newBoard = board.map(r => [...r]);
          newBoard[aiMove.row][aiMove.col] = aiMove.letter;
          const newPatterns = checkSOSPatterns(newBoard, boardSize, aiMove.row, aiMove.col);
          const pts = newPatterns.length;
          const gameOver = isSOSGameOver(newBoard, boardSize);
          setBoard(newBoard);
          setMoveHistory(prev => [...prev, { row: aiMove.row, col: aiMove.col, letter: aiMove.letter, player: 2, board: board.map(r => [...r]) }]);
          if (pts > 0) {
            const newScores = [...sosScores];
            newScores[1] += pts;
            setSosScores(newScores);
            setSosPatterns(getAllSOSPatterns(newBoard, boardSize));
            setSosConfetti(true);
            setTimeout(() => setSosConfetti(false), 2500);
            toast.success(`AI scored ${pts} SOS!`);
            if (gameOver) finishSOSGame(newScores);
            // SOS rule: AI keeps turn after scoring
          } else if (gameOver) {
            // Board full without scoring — finalize without advancing turn
            finishSOSGame(sosScores);
          } else {
            setSosCurrentPlayer(1);
          }
        }
        setIsAIThinking(false);
      }, thinkTime);
      return () => clearTimeout(tid);
    }
  }, [sosCurrentPlayer, playerType, winner, gameStarted, mode, board, boardSize, aiDifficulty, soundEnabled, sosScores, sosPlayerCount, finishSOSGame]);

  // Handle game end (classic/gomoku)
  const handleGameEnd = useCallback((result) => {
    setWinner(result.winner);
    setWinningCells(result.winningCells);
    if (result.winner === "X") setXWins(prev => prev + 1);
    else if (result.winner === "O") setOWins(prev => prev + 1);
    else setDraws(prev => prev + 1);
    localStorage.removeItem('tictactoe_savedGame');
    setHasSavedGame(false);
    if (soundEnabled) {
      setTimeout(() => {
        if (result.winner === "draw") announceDraw();
        else {
          const winnerName = playerType === "pvp"
            ? (result.winner === "X" ? playerXName : playerOName)
            : (result.winner === "X" ? "Kamu" : "AI");
          announceWinner(winnerName);
        }
      }, 500);
    }
    setTimeout(() => {
      setShowWinnerModal(true);
    }, 500);
  }, [soundEnabled, playerType, playerXName, playerOName]);

  // Classic/Gomoku cell click
  const handleCellClick = useCallback((row, col) => {
    if (winner || board[row][col] || isAIThinking) return;
    if (playerType === "ai" && currentPlayer === "O") return;
    if (soundEnabled) { playThunderSound(); playZapSound(); }
    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = currentPlayer;
    setBoard(newBoard);
    setMoveHistory([...moveHistory, { row, col, player: currentPlayer, board: board.map(r => [...r]) }]);
    const result = checkWinner(newBoard, boardSize, winLength);
    if (result) handleGameEnd(result);
    else {
      const next = currentPlayer === "X" ? "O" : "X";
      setCurrentPlayer(next);
      if (soundEnabled && playerType === "pvp") {
        const nextName = next === "X" ? playerXName : playerOName;
        setTimeout(() => announcePlayerTurn(nextName), 300);
      }
    }
  }, [board, currentPlayer, winner, playerType, boardSize, winLength, moveHistory, isAIThinking, soundEnabled, playerXName, playerOName, handleGameEnd]);

  // AI move effect (classic/gomoku)
  useEffect(() => {
    if (gameStarted && playerType === "ai" && currentPlayer === "O" && !winner && board.length > 0) {
      setIsAIThinking(true);
      const thinkTime = aiDifficulty === "easy" ? 300 : aiDifficulty === "medium" ? 500 : 800;
      const tid = setTimeout(() => {
        const aiMove = getAIMove(board, boardSize, winLength, aiDifficulty, "O");
        if (aiMove) {
          const [row, col] = aiMove;
          if (soundEnabled) { playThunderSound(); playZapSound(); }
          const newBoard = board.map(r => [...r]);
          newBoard[row][col] = "O";
          setBoard(newBoard);
          setMoveHistory([...moveHistory, { row, col, player: "O", board: board.map(r => [...r]) }]);
          const result = checkWinner(newBoard, boardSize, winLength);
          if (result) handleGameEnd(result);
          else setCurrentPlayer("X");
        }
        setIsAIThinking(false);
      }, thinkTime);
      return () => clearTimeout(tid);
    }
  }, [currentPlayer, playerType, winner, board, gameStarted, boardSize, winLength, aiDifficulty, soundEnabled, handleGameEnd]);

  const handleUndo = () => {
    if (moveHistory.length === 0 || winner) return;
    const movesToUndo = playerType === "ai" && moveHistory.length >= 2 ? 2 : 1;
    const lastState = moveHistory[moveHistory.length - movesToUndo];
    if (lastState) {
      setBoard(lastState.board);
      setCurrentPlayer(lastState.player);
      setMoveHistory(moveHistory.slice(0, -movesToUndo));
    }
  };

  const handleReset = () => { initializeBoard(); toast.info("Game reset!"); };
  const handleBackToMenu = () => { setGameStarted(false); setWinner(null); setShowWinnerModal(false); };
  const handlePlayAgain = () => { setShowWinnerModal(false); initializeBoard(); };

  const handleSaveScore = async (playerName) => {
    setShowNameInput(false);
    try {
      const duration = Math.floor((Date.now() - gameStartTime) / 1000);
      await axios.post(`${API}/game/record`, {
        player_name: playerName, mode, board_size: boardSize,
        opponent_type: playerType, ai_difficulty: playerType === "ai" ? aiDifficulty : null,
        winner, moves_count: moveHistory.length, duration_seconds: duration,
      });
      toast.success("Score saved to leaderboard!");
    } catch (error) {
      logger.error("Failed to save score:", error);
      toast.error("Failed to save score");
    }
  };

  return {
    gameStarted, board, currentPlayer, winner, winningCells, moveHistory,
    mode, setMode, playerType, setPlayerType, aiDifficulty, setAiDifficulty,
    boardSize, setBoardSize, playerXName, playerOName,
    showPlayerNamesDialog, xWins, oWins, draws, hasSavedGame,
    sosScores, sosPatterns, sosCurrentPlayer, sosPlayerCount, setSosPlayerCount, sosPlayerNames,
    showWinnerModal, showNameInput, isAIThinking, soundEnabled, setSoundEnabled, sosConfetti,
    winLength, initializeBoard, saveGame, loadSavedGame, resetScores, gameStartTime,
    handleStartGame, handlePlayerNamesSubmit, handleSOSCellClick,
    handleCellClick, handleUndo, handleReset, handleBackToMenu,
    handlePlayAgain, handleSaveScore,
  };
}
